import { useCallback, useContext } from 'react'
import SettingsContext from '../contexts/SettingsContext'
import { GameStatus } from '../generated/graphql'

interface DashboardFiltersHook {
  // Game Status filters
  getGameStatusFilter: (page: 'timeline' | 'gamesList') => GameStatus[]
  setGameStatusFilter: (
    page: 'timeline' | 'gamesList',
    value: GameStatus[]
  ) => void

  // Year filters
  getYearFilter: (page: 'timeline' | 'gamesList') => Date | null
  setYearFilter: (page: 'timeline' | 'gamesList', value: Date | null) => void

  // Search filters
  getSearchFilter: (page: 'gamesList') => string
  setSearchFilter: (page: 'gamesList', value: string) => void

  // Favourites management
  getFavourites: () => string[]
  isFavourite: (gameId: string) => boolean
  addFavourite: (gameId: string) => void
  removeFavourite: (gameId: string) => void
  toggleFavourite: (gameId: string) => void

  // Generic filter methods
  getFilter: (page: string, filterType: string) => any
  setFilter: (page: string, filterType: string, value: any) => void
}

const useDashboardFilters = (): DashboardFiltersHook => {
  const { settings, updateDashboardFilter, saveSettings } =
    useContext(SettingsContext)

  const getFilter = useCallback(
    (page: string, filterType: string): any => {
      return settings.dashboardFilters?.[
        filterType as keyof typeof settings.dashboardFilters
      ]?.[page as keyof any]
    },
    [settings.dashboardFilters]
  )

  const setFilter = useCallback(
    (page: string, filterType: string, value: any): void => {
      updateDashboardFilter(page, filterType, value)
    },
    [updateDashboardFilter]
  )

  const getGameStatusFilter = useCallback(
    (page: 'timeline' | 'gamesList'): GameStatus[] => {
      const filter = getFilter(page, 'gameStatus')
      // Return default values if filter is not found or is null
      if (!filter || !Array.isArray(filter)) {
        return [GameStatus.Draft, GameStatus.Anticipation, GameStatus.Live]
      }
      return filter
    },
    [getFilter]
  )

  const setGameStatusFilter = useCallback(
    (page: 'timeline' | 'gamesList', value: GameStatus[]): void => {
      setFilter(page, 'gameStatus', value)
    },
    [setFilter]
  )

  const getYearFilter = useCallback(
    (page: 'timeline' | 'gamesList'): Date | null => {
      return getFilter(page, 'year')
    },
    [getFilter]
  )

  const setYearFilter = useCallback(
    (page: 'timeline' | 'gamesList', value: Date | null): void => {
      setFilter(page, 'year', value)
    },
    [setFilter]
  )

  const getSearchFilter = useCallback(
    (page: 'gamesList'): string => {
      return getFilter(page, 'search') || ''
    },
    [getFilter]
  )

  const setSearchFilter = useCallback(
    (page: 'gamesList', value: string): void => {
      setFilter(page, 'search', value)
    },
    [setFilter]
  )

  const getFavourites = useCallback((): string[] => {
    return settings.favourites || []
  }, [settings.favourites])

  const isFavourite = useCallback(
    (gameId: string): boolean => {
      return getFavourites().includes(gameId)
    },
    [getFavourites]
  )

  const addFavourite = useCallback(
    (gameId: string): void => {
      if (!isFavourite(gameId)) {
        const newFavourites = [...getFavourites(), gameId]
        saveSettings({ ...settings, favourites: newFavourites })
      }
    },
    [isFavourite, getFavourites, saveSettings, settings]
  )

  const removeFavourite = useCallback(
    (gameId: string): void => {
      const newFavourites = getFavourites().filter((id) => id !== gameId)
      saveSettings({ ...settings, favourites: newFavourites })
    },
    [getFavourites, saveSettings, settings]
  )

  const toggleFavourite = useCallback(
    (gameId: string): void => {
      if (isFavourite(gameId)) {
        removeFavourite(gameId)
      } else {
        addFavourite(gameId)
      }
    },
    [isFavourite, addFavourite, removeFavourite]
  )

  return {
    getGameStatusFilter,
    setGameStatusFilter,
    getYearFilter,
    setYearFilter,
    getSearchFilter,
    setSearchFilter,
    getFavourites,
    isFavourite,
    addFavourite,
    removeFavourite,
    toggleFavourite,
    getFilter,
    setFilter,
  }
}

export default useDashboardFilters
